import type { DataCaptureView } from '@scandit/web-datacapture-core'
import {
  Anchor,
  Brush,
  Color,
  Feedback,
  FrameSourceState,
  MeasureUnit,
  NumberWithUnit,
  PointWithUnit,
} from '@scandit/web-datacapture-core'
import {
  type CapturedLabel,
  type LabelCapture,
  LabelCaptureAdvancedOverlay,
  LabelCaptureBasicOverlay,
  LabelCaptureFeedback,
  type LabelCaptureListener,
  type LabelCaptureSettings,
  LabelCaptureSettingsBuilder,
  LabelDefinition,
} from '@scandit/web-datacapture-label'
import type { LabelPreset } from '../LabelCaptureSettingsStore'
import type { BuildSettingsContext, ControllerDeps, PresetController, Teardown } from './PresetController'
import { createPriceShelfBadge, PriceShelfBadge } from './priceShelfBadge'

const PRICE_TEXT_FIELD_NAME = 'priceText'
const KEEP_PRICE_DIGITS_AS_PRINTED = 'none'

/** Shape of a `LabelCaptureSettingsBuilder.setLabelProperties` entry — untyped in the SDK's own surface. */
interface LabelPropertyPatch extends Record<string, unknown> {
  op: 'replace'
  sub_object: { type: 'field' | 'label'; name: string }
  path: string
  value: unknown
}

export class PriceShelfLabelController implements PresetController {
  readonly preset: LabelPreset = 'priceShelfLabel'

  private readonly customFeedback = Feedback.defaultFeedback
  private readonly seenTrackingIds = new Set<number>()

  constructor(private readonly deps: ControllerDeps) {}

  async pause(mode: LabelCapture): Promise<void> {
    await mode.setEnabled(false)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.Standby)
  }

  async resume(mode: LabelCapture): Promise<void> {
    this.seenTrackingIds.clear()
    await mode.setEnabled(true)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.On)
  }

  handleCapturedLabel(label: CapturedLabel): void {
    if (this.seenTrackingIds.has(label.trackingId)) return
    this.seenTrackingIds.add(label.trackingId)
    this.deps.listStore.addFromLabelFields(label.fields)
    this.customFeedback.emit()
  }

  async buildSettings(ctx: BuildSettingsContext): Promise<LabelCaptureSettings> {
    const labelDefinition = await LabelDefinition.createPriceCaptureDefinition('price-label')
    const builder = new LabelCaptureSettingsBuilder().addLabel(labelDefinition)

    if (!ctx.pricesIncludeDecimals) {
      // createPriceCaptureDefinition bakes postProcessingMode: "divideBy100" into the
      // priceText field and offers no public API to override a single field on the
      // definition it returns, so the only way to keep printed digits as-is is a
      // JSON-patch-style engine property patch.
      const patch: LabelPropertyPatch[] = [
        {
          op: 'replace',
          sub_object: { type: 'field', name: PRICE_TEXT_FIELD_NAME },
          path: '/postProcessingMode',
          value: KEEP_PRICE_DIGITS_AS_PRINTED,
        },
      ]
      builder.setLabelProperties(patch)
    }

    return builder.build()
  }

  async activate(mode: LabelCapture, view: DataCaptureView): Promise<Teardown> {
    // Suppress SDK auto-feedback; we emit a custom one per new label below.
    const silentFeedback = new LabelCaptureFeedback()
    silentFeedback.success = new Feedback(null, null)
    mode.feedback = silentFeedback

    const basicOverlay = await LabelCaptureBasicOverlay.withLabelCaptureForView(mode, view)
    const stroke = Color.fromHex('00EEFF')
    const fill = stroke.withAlpha(0.3)
    await basicOverlay.setDefaultLabelBrush(new Brush(fill, stroke, 2))

    this.seenTrackingIds.clear()
    const badgeCache = new Map<number, PriceShelfBadge>()

    // Per-label "(i) <price>" badge above the detected label.
    const advancedOverlay = await LabelCaptureAdvancedOverlay.withLabelCaptureForView(mode, view)
    advancedOverlay.listener = {
      viewForCapturedLabel: (_overlay, label) => createPriceShelfBadge(label, badgeCache),
      anchorForCapturedLabel: () => Anchor.TopCenter,
      offsetForCapturedLabel: (_overlay, _label, badgeView) => {
        const arrowHeight = badgeView instanceof PriceShelfBadge ? badgeView.arrowHeight : 0
        return new PointWithUnit(
          new NumberWithUnit(0, MeasureUnit.Pixel),
          new NumberWithUnit(-(badgeView.offsetHeight + arrowHeight), MeasureUnit.Pixel)
        )
      },
    }

    const listener: LabelCaptureListener = {
      didUpdateSession: (_mode, session) => {
        for (const label of session.capturedLabels) {
          this.handleCapturedLabel(label)
        }
      },
    }
    mode.addListener(listener)

    await mode.setEnabled(true)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.On)

    return async () => {
      badgeCache.clear()
      mode.removeListener(listener)
      await view.removeOverlay(advancedOverlay)
      await view.removeOverlay(basicOverlay)
    }
  }
}
